import { BOX_TYPE, GRAPH_INTERVAL } from "@const/pageResponse.constants";
import { UNIT } from "@const/subsystemMetricsResponse.constants";
import { IMetric } from "@dto/architecture.dto";
import { Box, GraphBase, GraphInterval, GraphTablePropertyItem, Metric, RegroupGraph } from "@dto/pageResponse.dto";
import { TimeseriesRequestParams, TimeseriesTopOrder } from "@dto/timeseriesRequest.dto";
import { ItemParent, TimeseriesMetadata } from "@dto/timeseriesResponse.dto";
import { UnitUtil } from "@dto/util/UnitUtil";
import { CommonSelectProps } from "@parts/dashboard/select/AbstractDashboardSelect";
import { capitalize } from "lodash-es";
import type { Datum, Layout, LayoutAxis, PlotData, PlotlyHTMLElement } from "plotly.js";
import { createContext } from "react";
import type Plot from "react-plotly.js";
import { ColorUtil } from "src/util/ColorUtil";
import { ThemeUtil } from "src/util/ThemeUtil";
import { IGridEntry } from "../grid/gridTypes";

export interface PlotCmp extends Plot {
  el: PlotEl;
  resizeHandler: () => void;
}

interface PlotEl extends PlotlyHTMLElement {
  _fullLayout: Layout;
  _fullData: PlotData[];
}

/**
 * Common graph component properties
 */
export interface CommonGraphProps extends CommonSelectProps {
  availableMetrics?: IMetric[];
  colorMap?: Record<string, string>;
  setMaxValue?: (value: number, interval: GraphInterval) => void;
  minY?: number;
}

export interface UidName {
  uid: string;
  name: string;
}

interface IKTM extends UidName {
  key: string;
  parent?: ItemParent;
  rendered: Record<string, Record<string, string | number>>;
  data: Record<string, TimeseriesMetadata>;
  updated: boolean;
  updateCount: number;
}

export interface PropsKTM {
  itemProps: Record<string, GraphTablePropertyItem>;
  separator: boolean;
}

/**
 * (K)omputed table metric row entry
 */
export class KTM<P extends PropsKTM = PropsKTM> implements IKTM, IGridEntry {
  static readonly ITEM_OTHERS = 'Others';
  static readonly ITEM_DELETED = 'Deleted (';
  static readonly ITEM_TOTAL = 'Total';

  key: string = null;
  uid: string = null;
  name: string = null;
  parent?: ItemParent = null;
  metric?: string = null;
  rendered: Record<string, Record<string, string | number>> = {};
  data: Record<string, TimeseriesMetadata> = {};
  props: Partial<P> = {};
  updated: boolean = null;
  updateCount = 0;

  constructor(other?: IKTM) {
    if (!other) return;
    for (const key in other) {
      if (Object.hasOwn(other, key) && Object.hasOwn(this, key)) {
        this[key] = other[key];
      }
    }
  }

  get noFilter() {
    return this.isTotal();
  }

  isOthers() {
    return KTM.isOthers(this.name, this.uid);
  }

  static isOthers(name: string, uid: string) {
    return uid === KTM.ITEM_OTHERS || uid == null && name === KTM.ITEM_OTHERS;
  }

  isDeleted() {
    return KTM.isDeleted(this.name, this.uid);
  }

  static isDeleted(name: string, uid: string) {
    return uid === KTM.ITEM_DELETED || uid == null && name?.startsWith(KTM.ITEM_DELETED);
  }

  isTotal() {
    return KTM.isTotal(this);
  }

  static isTotal(ktm: UidName) {
    return ktm.uid === KTM.ITEM_TOTAL || ktm.uid == null && ktm.name === KTM.ITEM_TOTAL;
  }

  isLast() {
    return this.isDeleted() || this.isOthers();
  }

  isFirst() {
    return this.isTotal();
  }
}

/**
 * Graph data for modal
 */
export interface GraphData {
  data: Partial<PlotData>[];
  tableData: KTM[];
  max: number;
  zoomTitle: string;
}

/**
 * Graph component state properties
 */
export interface GraphState {
  additionalYaxis: IMetric;
  deletedItems?: TimeseriesRequestParams['deleted'];
  endSec?: number;
  hoverMode: Layout['hovermode'];
  //layout: Layout;
  loadedItems: number;
  orderBy: TimeseriesTopOrder;
  othersTooltip: string;
  othersCount: number;
  peakMetric?: boolean;
  plotlyRef: PlotCmp;
  sortMetric?: string;
  stacked?: boolean;
  startSec?: number;
  tableRowKeys: string[];
  unitSize: string;
  uuids: string[];
}

export enum GraphType {
  regular, modal, dashboard
}

export interface StartEnd {
  start: number;
  end: number;
}

export interface LayoutProps {
  type: GraphType;
  title?: string;
  box: {
    aggregated?: boolean;
    graphLabel?: string;
    max?: number;
    metrics: Metric[];
    minInterval?: number;
    uuids?: string[];
  };
  data?: {
    zoomTitle?: string;
  };
}
export class GraphProps implements LayoutProps {
  title?: string;
  /**
   * Filename for saving plot as img or csv
   */
  filename?: string;
  box: GraphBase;
  /**
   * Unix seconds
   */
  start: number;
  /**
   * Unix seconds
   */
  end: number;
  interval?: GraphInterval;
  showLegend?: boolean;
  common?: CommonGraphProps;
  type: GraphType;
  data?: GraphData;
  state?: GraphState;
  onLoadFinished?: () => void;

  static isZoomAllowed(box: LayoutProps['box']) {
    return box.minInterval !== 0;
  }
}

function removeWords(str: string, words: string[]) {
  return words.reduce((prev, word) => prev.replace(word, ''), str).trim();
}

/**
 * Returns metric title used in Y-axis of graph
 * @param graphBase common DTO graph properties
 * @returns
 */
export function metricTitle(metrics: Metric[]) {
  // ! First letter casing is determined on the backend. Remove this comment and related lines below when confirmed.
  // const capitalized = TextUtil.capitalize((metrics[0].unitLabel || metrics[0].unit || '').replaceAll('_', ' '));
  let titleWithMetrics = (metrics[0].unitLabel || metrics[0].unit || '').replaceAll('_', ' ');

  if (metrics.length === 2) {
    const negativeMetric = metrics.find(m => m.order < 0);
    if (negativeMetric) {
      const positiveMetric = metrics.find(m => m.order >= 0);
      if (positiveMetric) {
        const words = titleWithMetrics.toLowerCase().split(' ');
        const dupes = negativeMetric.label.toLowerCase().split(' ').concat(positiveMetric.label.toLowerCase().split(' ')).reduce<string[]>((prev, current, index, array) => {
          if (array.lastIndexOf(current) !== index && !prev.includes(current)) prev.push(current);
          return prev;
        }, []);
        words.push(...dupes);
        titleWithMetrics = capitalize(removeWords(negativeMetric.label.toLocaleLowerCase(), words)) + '  -  '
          + titleWithMetrics + '  -  ' + capitalize(removeWords(positiveMetric.label.toLocaleLowerCase(), words));
      }
    }
  }

  return titleWithMetrics;
}

export const TICK_FORMAT_SI = '.3~s';
export const TICK_FORMAT_NO_SI = '.3~r';
/**
 * Workaround for Plotly 0 tick showing as float e.g. 0.00000044
 */
export const TICK_FORMAT_FIXED = '.5~f';

export function createYaxis(metrics: Metric[]): Partial<LayoutAxis> {
  return {
    //fixedrange: true, !GraphProps.isZoomAllowed(props.box),
    gridcolor: ThemeUtil.getBgColorLight(),
    zerolinecolor: ThemeUtil.isDarkMode() ? '#888' : undefined,
    tickformat: UnitUtil.isBaseUnit(metrics[0].unit) ? TICK_FORMAT_NO_SI : TICK_FORMAT_SI,
    title: {
      text: metricTitle(metrics),
      font: {
        size: 10
      },
      standoff: 10
    },
    rangemode: 'tozero',
  };
}

export const X_HOVER_TIME_FORMAT = "%Y-%m-%d %H:%M";
export function createPlotlyLayout(props: LayoutProps) {
  const initialLayout: Partial<Layout> = {
    autosize: true,
    font: {
      size: 10,
      color: ThemeUtil.getColor()
    },
    //hoverdistance: 1,
    hoverlabel: {
      bgcolor: ThemeUtil.getBgColor("white"),
      font: { size: 11, color: ThemeUtil.getColor() },
      align: "left",
    },
    hovermode: 'closest',
    legend: {
      x: 1,
      xanchor: 'right',
      y: 1
    },
    margin: { t: 25, b: 25, l: 60, r: 10 },
    paper_bgcolor: ThemeUtil.getBgColor(),
    plot_bgcolor: ThemeUtil.getBgColor(),
    showlegend: false,
    spikedistance: 0,
    title: {
      text: props.data?.zoomTitle || props.box.graphLabel || props.title,
    },
    xaxis: {
      fixedrange: !GraphProps.isZoomAllowed(props.box),
      gridcolor: ThemeUtil.getBgColorLight(),
      hoverformat: X_HOVER_TIME_FORMAT,
      tickformatstops: [
        {
          dtickrange: [null, 1000 * 60 * 59],
          value: '%H:%M'
        },
        {
          dtickrange: [null, 1000 * 60 * 60 * 12],
          value: '%H'
        },
        {
          dtickrange: [null, 1000 * 60 * 60 * 24],
          value: '%a'
        },
        {
          dtickrange: [null, 1000 * 60 * 60 * 24 * 14],
          value: '%d'
        },
        {
          dtickrange: [null, 1000 * 60 * 60 * 24 * 180],
          value: '%b'
        }
      ],
      tickmode: 'auto',
      type: 'date',
      showticklabels: !!props.box.uuids?.length
    },
    yaxis: createYaxis(props.box.metrics),
  };

  if (ColorUtil.colorPalette) {
    initialLayout.colorway = ColorUtil.colorPalette;
  }

  if (props.box.metrics.some(m => m.unit === UNIT.percent && m.maxY !== true)) {
    initialLayout.yaxis.range = [0, 101];
  }

  switch (props.type) {
    case GraphType.dashboard:
      initialLayout.margin = { b: 15, l: 15, r: 0, t: 0 };
      initialLayout.yaxis.automargin = true;
      initialLayout.yaxis.fixedrange = true;
      initialLayout.title = null;
      initialLayout.yaxis.title = null;
      initialLayout.yaxis.tickformat = TICK_FORMAT_SI;
      initialLayout.xaxis.fixedrange = true;
      break;
    case GraphType.regular:
      initialLayout.height = 250;
      break;
    default:
      initialLayout.height = window.innerHeight * 0.6;
  }

  return initialLayout;
}

/******************* REGROUP ******************************/

export class RegroupedGraphBox implements Box {
  type = BOX_TYPE.RegroupedGraphBox;
  interval: GraphInterval;
  graphs: RegroupGraph[];
  url: string;
}

export const RegroupedGraphIntervalLabel = {
  [GRAPH_INTERVAL.day]: 'Daily',
  [GRAPH_INTERVAL.week]: 'Weekly',
  [GRAPH_INTERVAL.month]: 'Monthly',
  [GRAPH_INTERVAL.year]: 'Yearly'
};

export interface RegroupProps {
  /**
   * Zoom start/end in unix seconds
   */
  readonly zoomRange: Readonly<StartEnd>;
  setZoomRange(range: StartEnd): void;
  /**
   * Keys of graph items hidden in plot
   */
  readonly hiddenItems: readonly string[];
  setHiddenItems(itemKeys: string[]): void;
  hover?: {
    sourceID: string;
    xval: Datum;
    setVals: (xval: Datum, sourceID: string) => void;
  };
}

export const RegroupContext = createContext<RegroupProps>({
  zoomRange: undefined,
  setZoomRange: () => undefined,
  hiddenItems: [],
  setHiddenItems: () => undefined
});

